import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import { IPublicClientApplication } from '@azure/msal-browser';
import * as TeamsState from './TeamsState';
import { TokenHelper } from '../TokenHelper';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface UserLoginState {
    isLoading: boolean;
    rigths: UserLoginRights | null;
    graphToken: string | null | undefined;
    graphTokenLoading: boolean;
    viewDebugInfo: boolean;
    viewAdminInfo: boolean;
    periodeFilter: number;
    yearFilter: number;
}

export interface UserLoginRights {
    isAdmin: boolean;
    canEnterTimeSheet: boolean;
    canEnterDocuments: boolean;
    canManageConsultantDocuments: boolean;
    seeDebugInfo: boolean;
    consultantid: string;
    version: number;
    buildTime: string|null;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

interface RequestUserLoginAction {
    type: 'REQUEST_USER_LOGIN';
}

interface ReceiveUserLoginAction {
    type: 'RECEIVE_USER_LOGIN';
    rigths: UserLoginRights;
}

interface ReceiveUserLoginCanceledAction {
    type: 'RECEIVE_USER_LOGIN_CANCELED';
}

interface ReceiveAccessDenyAction {
    type: 'RECEIVE_USER_LOGIN_ACCESSDENIED';
}

interface RequestGraphTokenAction {
    type: 'REQUEST_GRAPHTOKEN';
}

interface ReceiveGraphTokenAction {
    type: 'RECEIVE_GRAPHTOKEN';
    graphToken: string | null;
}

interface ToogleDebugViewAction {
    type: 'TOOGLEDEBUGVIEW';
}

interface ToogleAdminViewAction {
    type: 'TOOGLEADMINVIEW';
}

interface ChangePeriodeYearFilterAction {
    type: 'CHANGEPERIODEYEARFILTER';
    periodeFilter: number;
    yearFilter: number;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction = RequestUserLoginAction | ReceiveUserLoginAction | ReceiveUserLoginCanceledAction | ReceiveAccessDenyAction
    | RequestGraphTokenAction | ReceiveGraphTokenAction
    | ToogleDebugViewAction | ToogleAdminViewAction | ChangePeriodeYearFilterAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const UserLoginActionCreators = {
    requestUserLogin: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownAction> => (dispatch/*, getState*/) => {
        // Only load data if it's something we don't already have (and are not already loading)
        dispatch({ type: 'REQUEST_USER_LOGIN' });
        TokenHelper.GetToken(msalInstance, teamsState)
            .then(token => {
                if (token !== null) {
                    fetch(`api/Login`, {
                        method: "get",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 400 || response.status === 401 || response.status === 403) {
                                dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                            }
                            return response.json() as Promise<UserLoginRights>;
                        })
                        .then(data => {
                            dispatch({ type: 'RECEIVE_USER_LOGIN', rigths: data });
                        });
                }
                else {
                    dispatch({ type: 'RECEIVE_USER_LOGIN_CANCELED' });
                }
            });
    },
    requestGraphToken: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownAction> => (dispatch/*, getState*/) => {
        // Only load data if it's something we don't already have (and are not already loading)
        dispatch({ type: 'REQUEST_GRAPHTOKEN' });
        TokenHelper.GetToken(msalInstance, teamsState)
            .then(token => {
                if (token !== null) {
                    fetch(`api/Auth`, {
                        method: "get",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 403) {
                                dispatch({ type: 'RECEIVE_GRAPHTOKEN', graphToken: null });
                            }
                            return response.text();
                        })
                        .then(data => {
                            dispatch({ type: 'RECEIVE_GRAPHTOKEN', graphToken: data });
                        });
                }
                else {
                    dispatch({ type: 'RECEIVE_GRAPHTOKEN', graphToken: null });
                }
            });
    },
    requestToogleDebugView: (): AppThunkAction<KnownAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'TOOGLEDEBUGVIEW' });
    },
    requestToogleAdminView: (): AppThunkAction<KnownAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'TOOGLEADMINVIEW' });
    },
    requestChangePeriodeYearFilter: (periodeFilter: number, yearFilter: number): AppThunkAction<KnownAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'CHANGEPERIODEYEARFILTER', periodeFilter: periodeFilter, yearFilter: yearFilter });
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

const unloadedState: UserLoginState = { rigths: null, isLoading: false, graphToken: undefined, graphTokenLoading: false, viewDebugInfo: false, viewAdminInfo:false, periodeFilter: 0, yearFilter: new Date().getFullYear() };

export const reducer: Reducer<UserLoginState> = (state: UserLoginState | undefined, incomingAction: Action): UserLoginState => {
    if (state === undefined) {
        return unloadedState;
    }

    const action = incomingAction as KnownAction;
    switch (action.type) {
        case 'REQUEST_USER_LOGIN':
            return { ...state, isLoading: true };
        case 'RECEIVE_USER_LOGIN':
            return { ...state, rigths: action.rigths, isLoading: false, viewAdminInfo: action.rigths.isAdmin };
        case 'RECEIVE_USER_LOGIN_ACCESSDENIED':
            return { ...state, rigths: { isAdmin: false, canEnterTimeSheet: false, seeDebugInfo: false, canEnterDocuments: false, canManageConsultantDocuments: false, consultantid: "", version: 0, buildTime:null }, isLoading: false };
        case 'RECEIVE_USER_LOGIN_CANCELED':
            return { ...state, isLoading: false };
        case 'REQUEST_GRAPHTOKEN':
            return { ...state, graphTokenLoading: true };
        case 'RECEIVE_GRAPHTOKEN':
            return { ...state, graphTokenLoading: false, graphToken: action.graphToken };
        case 'TOOGLEDEBUGVIEW':
            return { ...state, viewDebugInfo: !state.viewDebugInfo };
        case 'TOOGLEADMINVIEW':
            return { ...state, viewAdminInfo: !state.viewAdminInfo };
        case 'CHANGEPERIODEYEARFILTER':
            return { ...state, periodeFilter: action.periodeFilter, yearFilter: action.yearFilter };
        default:
            return state;
    }
};